import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "@entry/navigation";

import { Info } from "@assets";
import { ErrorComponent } from "@components/ErrorComponent";
import { Row } from "@components/Flex";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { DateFormat, Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import {
  useErrorBanner,
  useOnboarding,
  usePrefill,
  useSetUserIdentity,
  useUser,
  useValidateUserPrefill,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { getFullName } from "@utils";
import { useStyles } from "./styles";

export const ReviewProfileRoute = () => {
  const styles = useStyles();

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { showErrorBanner } = useErrorBanner();
  const { resetToNext } = useOnboarding();

  const { user, loading: userLoading, error: userError } = useUser();
  const { prefillProps } = usePrefill();

  const [validateUserPrefill] = useValidateUserPrefill();
  const [setUserIdentity] = useSetUserIdentity();

  if (userLoading) return <Loader />;
  if (userError) return <ErrorComponent />;

  const {
    firstName,
    lastName,
    address: street,
    city,
    state,
    address2: unitNo,
    zipCode: postalCode,
    ssn,
    country,
    residenceStatus,
  } = prefillProps;

  const email = prefillProps.email ?? user?.email;
  const phoneNumber = prefillProps.phoneNumber ?? user?.phoneNumber;

  const fullName = getFullName(firstName, lastName);

  const dateOfBirth = dayjs(user?.dateOfBirth).format(DateFormat.User);
  const dateOfBirthDisplay = dayjs(user?.dateOfBirth).format(DateFormat.FullDate);

  const fullAddress = [street, city, state, postalCode].join(", ");

  const handleSubmit = async () => {
    const isMissingProp =
      !firstName ||
      !lastName ||
      !phoneNumber ||
      !street ||
      !city ||
      !state ||
      !postalCode ||
      !ssn ||
      !email ||
      !country;

    if (isMissingProp) return showErrorBanner();

    try {
      setLoading(true);

      await validateUserPrefill({
        variables: {
          firstName,
          lastName,
          email,
          phoneNumber,
          dateOfBirth,
          address: {
            street,
            city,
            state,
            postalCode,
            unitNo,
            country,
          },
          ssn,
        },
      });

      if (country && residenceStatus) {
        await setUserIdentity({
          variables: {
            countryOfCitizenship: country,
            residenceStatus,
          },
        });
      }

      resetToNext();
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.ReviewProfileRouteTitle),
    subtitle: i18n.t(Translations.ReviewProfileRouteSubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.ReviewProfileRoutePrimaryCTA),
        onClick: handleSubmit,
        loading,
      },
      secondary: {
        title: i18n.t(Translations.ReviewProfileRouteSecondaryCTA),
        onClick: () => navigate(Routes.Prefill),
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <div style={styles.container}>
        <Body style={styles.subheader}>{i18n.t(Translations.ReviewProfileRouteFullName)}</Body>
        <Body>{fullName}</Body>
        <Spacing size={SpacingSizes.XS} />

        <Body style={styles.subheader}>{i18n.t(Translations.ReviewProfileRouteDateOfBirth)}</Body>
        <Body>{dateOfBirthDisplay}</Body>
        <Spacing size={SpacingSizes.XS} />

        <Body style={styles.subheader}>{i18n.t(Translations.ReviewProfileRouteAddress)}</Body>
        <Body>{fullAddress}</Body>
        <Spacing size={SpacingSizes.XS} />

        <Body style={styles.subheader}>{i18n.t(Translations.ReviewProfileRouteCitizenship)}</Body>
        <Body>{country}</Body>
      </div>
      <Spacing size={SpacingSizes.M} />

      <Row style={styles.footerContainer}>
        <Body style={styles.footer}>{i18n.t(Translations.ReviewProfileRouteInfo)}</Body>
        <Info size={18} style={styles.info} />
      </Row>

      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
